import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  type OnInit,
  Output,
  type QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { isNotBlank } from '@bizzdesign/bizzdesign-sdk';
import { generateUUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  defaultAdvancedFilter,
  defaultAttributeFilterValue,
  defaultRelationFilterValue,
  type FilterOption,
} from '@bizzdesign/data-access-search';
import { type ObjectType } from '@bizzdesign/sdk-knowledgegraph/bdcore';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import {
  type AdvancedFilter,
  type AttributeFilterValue,
  type AuthenticatedUser,
  DefaultDialogSize,
  type DialogProperties,
  type RelationFilterValue,
  type SystemAttributesFilter,
  type SystemAttributesUser,
} from '@bizzdesign/shared/models';
import { type SearchScope } from '@bizzdesign/sdk-search';
import { DialogComponent } from '@bizzdesign/ui-components';
import { TranslocoService } from '@jsverse/transloco';
import { cloneDeep, isEmpty } from 'lodash-es';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { DataViewModule } from 'primeng/dataview';
import { DividerModule } from 'primeng/divider';
import { BehaviorSubject, map, type Observable, of } from 'rxjs';
import { AdvancedFilterAttributeComponent } from '../advanced-filter-attribute/advanced-filter-attribute.component';
import { AdvancedFilterOptionsComponent } from '../advanced-filter-options/advanced-filter-options.component';
import { AdvancedFilterPeopleComponent } from '../advanced-filter-people/advanced-filter-people.component';
import { AdvancedFilterRelationshipComponent } from '../advanced-filter-relationship/advanced-filter-relationship.component';

@Component({
  selector: 'bd-advanced-filter',
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    AccordionModule,
    AdvancedFilterOptionsComponent,
    FormsModule,
    SharedI18nModule,
    ButtonModule,
    DialogComponent,
    DataViewModule,
    DividerModule,
    AdvancedFilterAttributeComponent,
    AdvancedFilterPeopleComponent,
    AdvancedFilterRelationshipComponent,
  ],
  templateUrl: './advanced-filter.component.html',
  styleUrl: './advanced-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedFilterComponent implements OnInit {
  private translocoService = inject(TranslocoService);

  @Input() layout: 'grid' | 'list' = 'grid';
  @Input() visible: boolean = false;
  @Input() scopeFilterOptions: FilterOption<SearchScope>[] = [];
  @Input() users: SystemAttributesUser[] = [];
  @Input() elementTypeFilterOptions: FilterOption<ObjectType>[] = [];
  @Input() totalSearchResultsCount: number = 0;
  @Input() searchResultsCountLoading: boolean = false;
  @Input() usersLoading: boolean = false;
  @Input() authenticatedUser: AuthenticatedUser | undefined;
  @Input() set selectedAdvancedFilter(selectedAdvancedFilter: AdvancedFilter) {
    this._selectedAdvancedFilter = cloneDeep(selectedAdvancedFilter);
    this.originalSelectedFilter = cloneDeep(selectedAdvancedFilter);
  }
  get selectedAdvancedFilter(): AdvancedFilter {
    return this._selectedAdvancedFilter;
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() advancedFilterChanged: EventEmitter<AdvancedFilter> =
    new EventEmitter();
  @Output() advancedFilterForCountChanged: EventEmitter<AdvancedFilter> =
    new EventEmitter();
  @Output() searchUsers: EventEmitter<string> = new EventEmitter();

  @ViewChild('defaultAttributeFilter', { read: ElementRef })
  defaultAttributeFilter: ElementRef | undefined;

  @ViewChildren('selectedAttributeFilter', { read: ElementRef })
  set selectedAttributeFilter(content: QueryList<ElementRef>) {
    if (content?.length > 0) {
      this.defaultAttributeFilter?.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  @ViewChild('defaultRelationFilter', { read: ElementRef })
  defaultRelationFilter: ElementRef | undefined;

  @ViewChildren('selectedRelationFilter', { read: ElementRef })
  set selectedRelationFilter(content: QueryList<ElementRef>) {
    if (content?.length > 0) {
      this.defaultRelationFilter?.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  clonedDefaultRelationFilterValue: RelationFilterValue = cloneDeep(
    defaultRelationFilterValue,
  );
  clonedDefaultAttributeFilterValue: AttributeFilterValue = cloneDeep(
    defaultAttributeFilterValue,
  );
  clearSelectedFilters$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  dialogProperties$: Observable<DialogProperties> = of();

  filteredScopeFilterOptions$ = this.clearSelectedFilters$.pipe(
    map((clearSelectedFilter) =>
      clearSelectedFilter
        ? this.scopeFilterOptions.map((scopeFilterOption) => ({
            ...scopeFilterOption,
            checked: false,
          }))
        : this.scopeFilterOptions,
    ),
  );

  private originalSelectedFilter: AdvancedFilter = defaultAdvancedFilter;
  private _selectedAdvancedFilter: AdvancedFilter = defaultAdvancedFilter;

  ngOnInit(): void {
    this.dialogProperties$ = this.translocoService
      .selectTranslate('filter', undefined, 'featureSearch')
      .pipe(
        map((title) => ({
          defaultHeader: {
            headerTitle: title,
          },
          defaultSize: DefaultDialogSize.LARGE,
        })),
      );
  }

  updateElementTypeFilters(selectedElementTypeFilters: ObjectType[]) {
    this.selectedAdvancedFilter.elementTypeFilters = selectedElementTypeFilters;
    this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
  }

  updateSystemAttributesFilter(
    selectedSystemAttributesFilter: SystemAttributesFilter | undefined,
  ) {
    this.selectedAdvancedFilter.systemAttributesFilter =
      selectedSystemAttributesFilter;
    this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
  }

  updateScopeFilters(selectedScopeFilters: SearchScope[]) {
    this.selectedAdvancedFilter.scopeFilters = selectedScopeFilters;
    this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
  }

  onSubmitRelationFilter(selectedRelationFilter: RelationFilterValue) {
    const existingRelationFilterValid = selectedRelationFilter.isValid;

    selectedRelationFilter.isValid = this.isValidRelationFilter(
      selectedRelationFilter,
    );
    if (selectedRelationFilter.isValid) {
      if (selectedRelationFilter.id === '') {
        selectedRelationFilter.id = generateUUID();
        this.selectedAdvancedFilter.relationFilters.push(
          selectedRelationFilter,
        );
        this.clonedDefaultRelationFilterValue = cloneDeep(
          defaultRelationFilterValue,
        );
      } else {
        // If the filter was already in the list, we need to update it
        this.selectedAdvancedFilter.relationFilters =
          this.selectedAdvancedFilter.relationFilters.map((item) =>
            item.id === selectedRelationFilter.id
              ? selectedRelationFilter
              : item,
          );
      }

      this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
    } else if (existingRelationFilterValid) {
      // If the filter is invalid now, but was valid before, we need to emit the change
      this.selectedAdvancedFilter.relationFilters.forEach((item) => {
        if (item.id === selectedRelationFilter.id) {
          item.isValid = false;
        }
      });
      this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
    }
  }

  onSubmitAttributeFilter(selectedAttributeFilter: AttributeFilterValue) {
    const existingAttributeFilterValid = selectedAttributeFilter.isValid;

    selectedAttributeFilter.isValid = this.isValidAttributeFilter(
      selectedAttributeFilter,
    );

    if (selectedAttributeFilter.isValid) {
      if (selectedAttributeFilter.id === '') {
        selectedAttributeFilter.id = generateUUID();
        this.selectedAdvancedFilter.attributeFilters.push(
          selectedAttributeFilter,
        );
        this.clonedDefaultAttributeFilterValue = cloneDeep(
          defaultAttributeFilterValue,
        );
      } else {
        // If the filter was already in the list, we need to update it
        this.selectedAdvancedFilter.attributeFilters =
          this.selectedAdvancedFilter.attributeFilters.map((item) =>
            item.id === selectedAttributeFilter.id
              ? selectedAttributeFilter
              : item,
          );
      }

      this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
    } else if (existingAttributeFilterValid) {
      // If the filter is invalid now, but was valid before, we need to emit the change
      this.selectedAdvancedFilter.attributeFilters.forEach((item) => {
        if (item.id === selectedAttributeFilter.id) {
          item.isValid = false;
        }
      });
      this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
    }
  }

  isValidAttributeFilter(
    selectedAttributeFilter: AttributeFilterValue,
  ): boolean {
    const condition = selectedAttributeFilter.condition;

    if (condition === '') {
      return false;
    }

    if (condition === 'exists' || condition === 'not_exists') {
      return true;
    }

    return isNotBlank(selectedAttributeFilter.attribute.value?.toString());
  }

  isValidRelationFilter(selectedRelationFilter: RelationFilterValue): boolean {
    return (
      !!selectedRelationFilter.condition &&
      !!selectedRelationFilter.target &&
      (selectedRelationFilter.target === 'specificObject'
        ? !isEmpty(selectedRelationFilter.relation.relatedElement?.id)
        : true) &&
      (selectedRelationFilter.target === 'objectType'
        ? !isEmpty(selectedRelationFilter.relation.relatedElement?.type)
        : true)
    );
  }

  removeAttributeFilter(selectedAttributeFilter: AttributeFilterValue) {
    this.selectedAdvancedFilter.attributeFilters =
      this.selectedAdvancedFilter.attributeFilters.filter(
        (item) => item.id !== selectedAttributeFilter.id,
      );

    if (selectedAttributeFilter.isValid) {
      this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
    }
  }

  removeRelationFilter(selectedRelationFilter: RelationFilterValue) {
    this.selectedAdvancedFilter.relationFilters =
      this.selectedAdvancedFilter.relationFilters.filter(
        (item) => item.id !== selectedRelationFilter.id,
      );

    if (selectedRelationFilter.isValid) {
      this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
    }
  }

  clearFilters() {
    this.clearSelectedFilters$.next(true);
    this.selectedAdvancedFilter.attributeFilters = [];
    this.selectedAdvancedFilter.elementTypeFilters = [];
    this.selectedAdvancedFilter.scopeFilters = [];
    this.selectedAdvancedFilter.systemAttributesFilter = undefined;
    this.selectedAdvancedFilter.relationFilters = [];
    this.advancedFilterForCountChanged.emit(this.getAdvancedFilter());
  }

  cancel() {
    this.selectedAdvancedFilter = cloneDeep(this.originalSelectedFilter);
    this.setVisibility(false);
  }

  setVisibility(visible: boolean) {
    this.visibleChange.emit((this.visible = visible));
  }

  submitFilters() {
    this.advancedFilterChanged.emit(this.getAdvancedFilter());
    this.setVisibility(false);
  }

  private getAdvancedFilter(): AdvancedFilter {
    return {
      scopeFilters: this.selectedAdvancedFilter.scopeFilters,
      elementTypeFilters: this.selectedAdvancedFilter.elementTypeFilters,
      attributeFilters: this.selectedAdvancedFilter.attributeFilters.filter(
        (item) => item.isValid,
      ),
      relationFilters: this.selectedAdvancedFilter.relationFilters.filter(
        (item) => item.isValid,
      ),
      systemAttributesFilter:
        this.selectedAdvancedFilter.systemAttributesFilter,
    };
  }
}
